/**
 * usePointAssociationVisualization - Visualización de asociaciones punto-red en el mapa
 *
 * @remarks
 * Hook genérico para visualizar múltiples asociaciones de cualquier entidad punto
 * con la red canónica. Muestra:
 * - Líneas punteadas desde el punto hasta las coordenadas proyectadas
 * - Diferenciación visual entre primaria y secundarias
 * - Highlight de edges/nodes asociados
 * - Etiquetas de lado (IZQ/DER)
 *
 * @see ARQUITECTURA_ASOCIACION_PUNTO_RED.md
 */

import { useEffect, useRef, useState } from 'react';
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';
import type { NetworkPointAssociation } from '../services/point-association-api.service';

const SOURCE_ID = 'point-association-viz';
const LINE_LAYER_ID = 'point-association-line';
const LINE_SECONDARY_LAYER_ID = 'point-association-line-secondary';
const PROJECTED_POINT_LAYER_ID = 'point-association-point';
const PROJECTED_POINT_SECONDARY_LAYER_ID = 'point-association-point-secondary';
const SIDE_LABEL_LAYER_ID = 'point-association-side-label';

// Source de la red canónica (para feature-state)
const NETWORK_SOURCE_ID = 'canonical-network';

// Colores por estado (UX Sprint 6)
const STATUS_COLORS: Record<string, string> = {
  valid: '#22c55e',           // Verde
  weak: '#f59e0b',            // Amber
  needs_review: '#ef4444',    // Rojo
  needs_recalculation: '#3b82f6', // Azul
  recalculated: '#3b82f6',    // Azul
  orphaned: '#6b7280',        // Gris
  manual_lock: '#8b5cf6',     // Violeta
};

// Colores legacy (fallback)
const PRIMARY_COLOR = '#f59e0b'; // Amber
const SECONDARY_COLOR = '#64748b'; // Slate
const WARNING_COLOR = '#ef4444'; // Red para fuera de umbral

// Nota: Los helpers getStatusColor, getConfidenceOpacity, getConfidenceWidth
// fueron reemplazados por expresiones data-driven directamente en los layers

export interface UsePointAssociationVisualizationConfig {
  /** Asociaciones a visualizar (array, soporta múltiples) */
  associations: NetworkPointAssociation[];
  /** Coordenadas de la entidad origen [lng, lat] */
  sourceCoordinates: [number, number] | null;
  /** Si mostrar la visualización */
  visible: boolean;
}

/**
 * Hook para visualizar múltiples asociaciones de cualquier entidad punto con la red
 *
 * @example
 * ```tsx
 * usePointAssociationVisualization({
 *   associations: allAssociations,
 *   sourceCoordinates: tree.coordinates,
 *   visible: isSelected,
 * });
 * ```
 */
export function usePointAssociationVisualization({
  associations,
  sourceCoordinates,
  visible,
}: UsePointAssociationVisualizationConfig): void {
  const [layersLoaded, setLayersLoaded] = useState(false);
  const layersCreatedRef = useRef(false);
  const highlightedEdgeIdsRef = useRef<Set<string>>(new Set());
  const highlightedNodeIdsRef = useRef<Set<string>>(new Set());

  // ── Crear source y layers si no existen ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const setupLayers = (): void => {
      if (layersCreatedRef.current) {
        return;
      }
      layersCreatedRef.current = true;

      // 1. Crear source si no existe
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        });
      }

      // 2. Layer de líneas secundarias (debajo)
      // Usa colores por estado y opacidad por confianza (Sprint 6 UX)
      if (!map.getLayer(LINE_SECONDARY_LAYER_ID)) {
        map.addLayer({
          id: LINE_SECONDARY_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: ['all',
            ['==', ['geometry-type'], 'LineString'],
            ['==', ['get', 'isPrimary'], false],
          ],
          paint: {
            // Color basado en estado
            'line-color': ['case',
              ['==', ['get', 'status'], 'valid'], STATUS_COLORS.valid,
              ['==', ['get', 'status'], 'weak'], STATUS_COLORS.weak,
              ['==', ['get', 'status'], 'needs_review'], STATUS_COLORS.needs_review,
              ['==', ['get', 'status'], 'orphaned'], STATUS_COLORS.orphaned,
              ['==', ['get', 'status'], 'manual_lock'], STATUS_COLORS.manual_lock,
              ['==', ['get', 'withinThreshold'], false], WARNING_COLOR,
              SECONDARY_COLOR,
            ],
            // Grosor basado en confianza (data-driven)
            'line-width': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 1,
              0.5, 1.5,
              1, 2,
            ],
            // Opacidad basada en confianza
            'line-opacity': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 0.3,
              0.5, 0.5,
              1, 0.7,
            ],
            'line-dasharray': [2, 2],
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
            visibility: 'visible',
          },
        });
      }

      // 3. Layer de línea primaria (encima)
      // Usa colores por estado y opacidad por confianza (Sprint 6 UX)
      if (!map.getLayer(LINE_LAYER_ID)) {
        map.addLayer({
          id: LINE_LAYER_ID,
          type: 'line',
          source: SOURCE_ID,
          filter: ['all',
            ['==', ['geometry-type'], 'LineString'],
            ['==', ['get', 'isPrimary'], true],
          ],
          paint: {
            // Color basado en estado
            'line-color': ['case',
              ['==', ['get', 'status'], 'valid'], STATUS_COLORS.valid,
              ['==', ['get', 'status'], 'weak'], STATUS_COLORS.weak,
              ['==', ['get', 'status'], 'needs_review'], STATUS_COLORS.needs_review,
              ['==', ['get', 'status'], 'orphaned'], STATUS_COLORS.orphaned,
              ['==', ['get', 'status'], 'manual_lock'], STATUS_COLORS.manual_lock,
              ['==', ['get', 'withinThreshold'], false], WARNING_COLOR,
              PRIMARY_COLOR,
            ],
            // Grosor basado en confianza
            'line-width': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 1.5,
              0.5, 2,
              1, 3,
            ],
            // Opacidad basada en confianza
            'line-opacity': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 0.5,
              0.5, 0.7,
              1, 0.95,
            ],
            'line-dasharray': [4, 4],
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
            visibility: 'visible',
          },
        });
      }

      // 4. Layer de puntos secundarios (anclajes)
      // Colores por estado, tamaño por confianza (Sprint 6 UX)
      if (!map.getLayer(PROJECTED_POINT_SECONDARY_LAYER_ID)) {
        map.addLayer({
          id: PROJECTED_POINT_SECONDARY_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['all',
            ['==', ['geometry-type'], 'Point'],
            ['==', ['get', 'isPrimary'], false],
          ],
          paint: {
            // Tamaño basado en confianza
            'circle-radius': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 3,
              0.5, 4,
              1, 5,
            ],
            // Color basado en estado
            'circle-color': ['case',
              ['==', ['get', 'status'], 'valid'], STATUS_COLORS.valid,
              ['==', ['get', 'status'], 'weak'], STATUS_COLORS.weak,
              ['==', ['get', 'status'], 'needs_review'], STATUS_COLORS.needs_review,
              ['==', ['get', 'status'], 'orphaned'], STATUS_COLORS.orphaned,
              ['==', ['get', 'status'], 'manual_lock'], STATUS_COLORS.manual_lock,
              ['==', ['get', 'withinThreshold'], false], WARNING_COLOR,
              SECONDARY_COLOR,
            ],
            'circle-opacity': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 0.5,
              1, 0.8,
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': 0.8,
          },
          layout: {
            visibility: 'visible',
          },
        });
      }

      // 5. Layer del punto proyectado primario (anclaje principal)
      // Colores por estado, tamaño por confianza (Sprint 6 UX)
      if (!map.getLayer(PROJECTED_POINT_LAYER_ID)) {
        map.addLayer({
          id: PROJECTED_POINT_LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          filter: ['all',
            ['==', ['geometry-type'], 'Point'],
            ['==', ['get', 'isPrimary'], true],
          ],
          paint: {
            // Tamaño basado en confianza
            'circle-radius': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 4,
              0.5, 6,
              1, 8,
            ],
            // Color basado en estado
            'circle-color': ['case',
              ['==', ['get', 'status'], 'valid'], STATUS_COLORS.valid,
              ['==', ['get', 'status'], 'weak'], STATUS_COLORS.weak,
              ['==', ['get', 'status'], 'needs_review'], STATUS_COLORS.needs_review,
              ['==', ['get', 'status'], 'orphaned'], STATUS_COLORS.orphaned,
              ['==', ['get', 'status'], 'manual_lock'], STATUS_COLORS.manual_lock,
              ['==', ['get', 'withinThreshold'], false], WARNING_COLOR,
              PRIMARY_COLOR,
            ],
            'circle-opacity': ['interpolate', ['linear'], ['coalesce', ['get', 'confidence'], 1],
              0, 0.6,
              1, 0.95,
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-opacity': 1,
          },
          layout: {
            visibility: 'visible',
          },
        });
      }

      // 6. Layer de etiqueta de lado (solo primaria)
      // Halo color basado en estado (Sprint 6 UX)
      if (!map.getLayer(SIDE_LABEL_LAYER_ID)) {
        map.addLayer({
          id: SIDE_LABEL_LAYER_ID,
          type: 'symbol',
          source: SOURCE_ID,
          filter: ['all',
            ['==', ['geometry-type'], 'Point'],
            ['==', ['get', 'isPrimary'], true],
          ],
          layout: {
            'text-field': ['get', 'sideLabel'],
            'text-size': 11,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-offset': [0, -1.5],
            'text-anchor': 'center',
            visibility: 'visible',
          },
          paint: {
            'text-color': '#ffffff',
            // Halo color basado en estado
            'text-halo-color': ['case',
              ['==', ['get', 'status'], 'valid'], STATUS_COLORS.valid,
              ['==', ['get', 'status'], 'weak'], STATUS_COLORS.weak,
              ['==', ['get', 'status'], 'needs_review'], STATUS_COLORS.needs_review,
              ['==', ['get', 'status'], 'orphaned'], STATUS_COLORS.orphaned,
              ['==', ['get', 'status'], 'manual_lock'], STATUS_COLORS.manual_lock,
              PRIMARY_COLOR,
            ],
            'text-halo-width': 2,
          },
        });
      }

      setLayersLoaded(true);
    };

    if (map.isStyleLoaded()) {
      setupLayers();
    } else {
      const handleReady = (): void => {
        setupLayers();
      };
      map.once('idle', handleReady);
      map.once('styledata', () => {
        if (!layersCreatedRef.current) {
          setupLayers();
        }
      });
    }

    // Cleanup
    return () => {
      const currentMap = mapEngine.getMap();
      if (currentMap) {
        // Limpiar highlights de edges
        highlightedEdgeIdsRef.current.forEach((id) => {
          try {
            currentMap.removeFeatureState(
              { source: NETWORK_SOURCE_ID, id },
              'associated',
            );
          } catch {
            // Ignorar
          }
        });
        // Limpiar highlights de nodes
        highlightedNodeIdsRef.current.forEach((id) => {
          try {
            currentMap.removeFeatureState(
              { source: NETWORK_SOURCE_ID, id },
              'associated',
            );
          } catch {
            // Ignorar
          }
        });

        // Remover layers y source
        const layersToRemove = [
          SIDE_LABEL_LAYER_ID,
          PROJECTED_POINT_LAYER_ID,
          PROJECTED_POINT_SECONDARY_LAYER_ID,
          LINE_LAYER_ID,
          LINE_SECONDARY_LAYER_ID,
        ];
        layersToRemove.forEach((layerId) => {
          if (currentMap.getLayer(layerId)) {
            currentMap.removeLayer(layerId);
          }
        });
        if (currentMap.getSource(SOURCE_ID)) {
          currentMap.removeSource(SOURCE_ID);
        }
        setLayersLoaded(false);
        layersCreatedRef.current = false;
      }
    };
  }, []);

  // ── Actualizar datos cuando cambian las asociaciones ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoaded) return;

    const source = map.getSource(SOURCE_ID) as GeoJSONSource;
    if (!source) return;

    // 1. Limpiar highlights anteriores
    highlightedEdgeIdsRef.current.forEach((id) => {
      try {
        map.removeFeatureState(
          { source: NETWORK_SOURCE_ID, id },
          'associated',
        );
      } catch {
        // Ignorar
      }
    });
    highlightedEdgeIdsRef.current.clear();

    highlightedNodeIdsRef.current.forEach((id) => {
      try {
        map.removeFeatureState(
          { source: NETWORK_SOURCE_ID, id },
          'associated',
        );
      } catch {
        // Ignorar
      }
    });
    highlightedNodeIdsRef.current.clear();

    // 2. Si no hay asociaciones o no es visible, limpiar
    if (!associations || associations.length === 0 || !visible || !sourceCoordinates) {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      });
      return;
    }

    // 3. Filtrar asociaciones válidas
    const validAssociations = associations.filter(
      (a) => a.associationType !== 'none' && a.projectedCoordinate
    );

    if (validAssociations.length === 0) {
      source.setData({
        type: 'FeatureCollection',
        features: [],
      });
      return;
    }

    // 4. Crear features para todas las asociaciones
    const features: GeoJSON.Feature[] = [];

    validAssociations.forEach((association) => {
      const projectedCoords = association.projectedCoordinate!;
      const isPrimary = association.isPrimary ?? false;
      const withinThreshold = association.withinThreshold !== false;
      // Sprint 6 UX: Incluir estado y confianza para estilos dinámicos
      const status = association.status || 'valid';
      const confidence = association.confidenceScore ?? 1;
      const projectionType = association.projectionType || 'orthogonal_inside';
      const distance = association.distanceToNetwork ?? 0;

      // Determinar etiqueta de lado (solo para primaria)
      const sideOfEdge = association.sideOfEdge || 'unknown';
      const sideLabel =
        isPrimary
          ? sideOfEdge === 'left'
            ? 'IZQ'
            : sideOfEdge === 'right'
              ? 'DER'
              : ''
          : '';

      // Línea de conexión (con propiedades de estado y confianza)
      features.push({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [sourceCoordinates, projectedCoords],
        },
        properties: {
          type: 'association-line',
          isPrimary,
          withinThreshold,
          connectionId: association.id,
          // Sprint 6 UX properties
          status,
          confidence,
          projectionType,
          distance,
        },
      });

      // Punto proyectado (anclaje) con propiedades de estado y confianza
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: projectedCoords,
        },
        properties: {
          type: 'projected-point',
          isPrimary,
          withinThreshold,
          sideOfEdge,
          sideLabel,
          connectionId: association.id,
          // Sprint 6 UX properties
          status,
          confidence,
          projectionType,
          distance,
          entityType: association.entityType,
          entityId: association.entityId,
        },
      });

      // 5. Aplicar highlight al edge/node asociado
      if (association.associationType === 'edge' && association.edgeId) {
        try {
          map.setFeatureState(
            { source: NETWORK_SOURCE_ID, id: association.edgeId },
            { associated: true },
          );
          highlightedEdgeIdsRef.current.add(association.edgeId);
        } catch {
          // Ignorar
        }
      } else if (association.associationType === 'node' && association.nodeId) {
        try {
          map.setFeatureState(
            { source: NETWORK_SOURCE_ID, id: association.nodeId },
            { associated: true },
          );
          highlightedNodeIdsRef.current.add(association.nodeId);
        } catch {
          // Ignorar
        }
      }
    });

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  }, [associations, sourceCoordinates, visible, layersLoaded]);

  // ── Actualizar visibilidad de layers ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoaded) return;

    const visibility = visible ? 'visible' : 'none';
    const layersToUpdate = [
      LINE_LAYER_ID,
      LINE_SECONDARY_LAYER_ID,
      PROJECTED_POINT_LAYER_ID,
      PROJECTED_POINT_SECONDARY_LAYER_ID,
      SIDE_LABEL_LAYER_ID,
    ];

    layersToUpdate.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility);
      }
    });
  }, [visible, layersLoaded]);

  // ── Sprint 6 UX: Tooltips informativos en hover ──
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !layersLoaded || !visible) return;

    let popup: maplibregl.Popup | null = null;

    const showTooltip = (e: MapLayerMouseEvent): void => {
      if (!e.features || e.features.length === 0) return;

      const feature = e.features[0];
      const props = feature.properties;
      if (!props) return;

      // Crear contenido del tooltip
      const status = props.status || 'desconocido';
      const confidence = props.confidence !== undefined
        ? `${Math.round(props.confidence * 100)}%`
        : 'N/D';
      const distance = props.distance !== undefined
        ? `${props.distance.toFixed(1)}m`
        : 'N/D';
      const projectionType = props.projectionType === 'orthogonal_inside'
        ? 'Perpendicular'
        : props.projectionType === 'clamped_to_start'
        ? 'Extremo inicio'
        : props.projectionType === 'clamped_to_end'
        ? 'Extremo fin'
        : props.projectionType || 'N/D';
      const side = props.sideOfEdge === 'left'
        ? 'Izquierda'
        : props.sideOfEdge === 'right'
        ? 'Derecha'
        : props.sideOfEdge || 'Centro';
      const isPrimary = props.isPrimary ? 'Sí' : 'No';

      // Mapear estado a español con emoji
      const statusDisplay: Record<string, string> = {
        valid: '✓ Válida',
        weak: '⚡ Débil',
        needs_review: '⚠ Requiere revisión',
        orphaned: '⊗ Huérfana',
        needs_recalculation: '↻ Pendiente recálculo',
        manual_lock: '🔒 Bloqueada',
      };

      const html = `
        <div style="font-size: 12px; line-height: 1.4; min-width: 140px;">
          <div style="font-weight: 600; margin-bottom: 6px; border-bottom: 1px solid #444; padding-bottom: 4px;">
            Asociación
          </div>
          <div style="display: grid; grid-template-columns: auto 1fr; gap: 2px 8px;">
            <span style="color: #888;">Estado:</span>
            <span style="font-weight: 500;">${statusDisplay[status] || status}</span>
            <span style="color: #888;">Confianza:</span>
            <span>${confidence}</span>
            <span style="color: #888;">Distancia:</span>
            <span>${distance}</span>
            <span style="color: #888;">Proyección:</span>
            <span>${projectionType}</span>
            <span style="color: #888;">Lado:</span>
            <span>${side}</span>
            <span style="color: #888;">Primaria:</span>
            <span>${isPrimary}</span>
          </div>
        </div>
      `;

      // Obtener coordenadas del punto
      const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number];

      // Remover popup anterior si existe
      if (popup) {
        popup.remove();
      }

      // Crear nuevo popup
      popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'association-tooltip',
        maxWidth: '220px',
      })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(map);

      // Cambiar cursor
      map.getCanvas().style.cursor = 'pointer';
    };

    const hideTooltip = (): void => {
      if (popup) {
        popup.remove();
        popup = null;
      }
      map.getCanvas().style.cursor = '';
    };

    // Agregar listeners a las capas de puntos
    const pointLayers = [PROJECTED_POINT_LAYER_ID, PROJECTED_POINT_SECONDARY_LAYER_ID];

    pointLayers.forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.on('mouseenter', layerId, showTooltip);
        map.on('mouseleave', layerId, hideTooltip);
      }
    });

    return () => {
      pointLayers.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.off('mouseenter', layerId, showTooltip);
          map.off('mouseleave', layerId, hideTooltip);
        }
      });
      if (popup) {
        popup.remove();
      }
    };
  }, [visible, layersLoaded]);
}
